//***********************************************************
// Angular
//***********************************************************
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//***********************************************************
// MJ - Consolidated Module Bundles
//***********************************************************
import {
  MJExplorerModulesBundle,
  SharedService
} from '@memberjunction/ng-explorer-modules';
import { AuthServicesModule, RedirectComponent, MJAuthBase } from '@memberjunction/ng-auth-services';
import { MJExplorerAppModule } from '@memberjunction/ng-explorer-app';

// ForgeRock auth provider — side-effect import so its `@RegisterClass(MJAuthBase, 'forgerock')`
// decorator runs before `AuthServicesModule.forRoot(environment)` below is evaluated (Task 28).
// ES module imports execute in file order before this module's own top-level code (including the
// @NgModule class body) runs, so this only needs to appear anywhere above the `@NgModule` class —
// placed here, directly after the other auth-services imports, for readability. Registration is
// harmless either way: `forRoot` only instantiates the provider whose type matches
// `environment.AUTH_TYPE` (ClassFactory.GetRegistration in auth-services.module.js). This provider
// is selected when that value is `forgerock` — stage and dev since 2026-09-22, prod pending; an
// environment that has not been switched over simply leaves the class registered but unselected.
import './auth/forgerock-provider.service';
// Magic-link provider that clears MJ's client caches when the redeemed user differs from the user
// they were written for (upstream defect #15). Same side-effect-import rule; it must stay below the
// `@memberjunction/ng-auth-services` import so its 'magic-link' registration outranks MJ's.
import './auth/agu-magic-link-provider.service';

// Lazy loading infrastructure
import { LazyModuleRegistry, LAZY_FEATURE_CONFIG } from '@memberjunction/ng-explorer-core';

// Import lite class registrations manifest (excludes lazy-loaded dashboard and settings packages)
import {CLASS_REGISTRATIONS} from '@memberjunction/ng-bootstrap-lite';

// Import supplemental manifest for user-defined classes (generated at prestart with --exclude-packages @memberjunction)
import {CLASS_REGISTRATIONS as LOCAL_CLASSES} from './generated/class-registrations-manifest';

// static code path builder
const combinedClasses = [...CLASS_REGISTRATIONS, ...LOCAL_CLASSES];

//***********************************************************
//MSAL
//***********************************************************
import { MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';

//***********************************************************
// Project stuff
//***********************************************************
import { AppComponent } from './app.component';
import { GeneratedFormsModule } from './generated/generated-forms.module';
import { environment } from '../environments/environment';

// AGU custom Explorer extensions (registered via @RegisterClass decorators)
import { NavigationItemDemoComponent } from './demo/navigation-item.component';
import { HelloDashboardComponent } from './demo/hello-dashboard/hello-dashboard.component';
import { MapsSurfaceModule } from './maps/maps-surface.module';
import { OEPSurfaceModule } from './feedbackplatform/oep-surface.module';
import { RaSurfaceModule } from './research-accelerator/ra-surface.module';

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
  };
}

/**
 * Initialize auth provider before Angular routing starts
 * This ensures MSAL can process OAuth redirect responses before Angular's router
 * consumes the URL hash
 */
export function initializeAuth(authService: MJAuthBase): () => Promise<void> {
  return () => authService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    NavigationItemDemoComponent,
    HelloDashboardComponent,
  ],
  imports: [
    // Angular Core Modules
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,

    // MJ Consolidated Bundle (includes all MJ + Kendo modules)
    MJExplorerModulesBundle,

    // Auth (needs forRoot configuration)
    AuthServicesModule.forRoot(environment),

    // Explorer App Shell (includes login UI, validation, and mj-shell wrapper)
    MJExplorerAppModule.forRoot(environment),

    // App-specific modules
    GeneratedFormsModule,

    // MAPS hosted "app surface" experiment — registers MAPSAppSurface dashboard
    // + a dedicated /maps-graphql Apollo client. See plans/maps/MJE_SURFACE_EXPERIMENT.md
    MapsSurfaceModule,

    // OEP platform surface — registers the FeedbackPlatformAppSurface dashboard.
    // Uses MJ's own data provider (no second Apollo client).
    OEPSurfaceModule,

    // Research Accelerator — registers the ONE 'AGUAppSurface' driver class that
    // serves Grant Finder, People Finder, Job Finder and the composite app. Which
    // modules each app has is metadata (the Dashboard record's UIConfigDetails),
    // so a new app needs no change here. Uses MJ's own data provider.
    RaSurfaceModule
  ],
  providers: [
    SharedService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [MJAuthBase],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (lazyRegistry: LazyModuleRegistry) => () => {
        lazyRegistry.RegisterBulk(LAZY_FEATURE_CONFIG);
        lazyRegistry.WireToClassFactory();
      },
      deps: [LazyModuleRegistry],
      multi: true
    }
  ],
  bootstrap: [AppComponent, RedirectComponent],
})
export class AppModule {}
